import {noop} from '@benchling/js-utils';
import classNames from 'classnames';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import {BenchlingDispatch} from 'redux-stores/Store';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import * as WorkflowTemplateSelectionReduxActions from 'workflows/template-selection/WorkflowTemplateSelectionReduxActions';
import * as WorkflowTemplateUtil from 'workflows/util/WorkflowTemplateUtil';

import {WorkflowTemplateSelectionTableRow} from './WorkflowTemplateSelectionConstants';

interface DispatchProps {
  onSelectWorkflowTemplateVersion: (workflowTemplateId: string, templateVersionId: string) => void;
  onSelectWorkflowTemplate: (workflowTemplateId: string) => void;
}

function mapDispatchToProps(dispatch: BenchlingDispatch): DispatchProps {
  return {
    onSelectWorkflowTemplateVersion(workflowTemplateId: string, workflowTemplateVersionId: string): void {
      dispatch(
        WorkflowTemplateSelectionReduxActions.selectWorkflowTemplateVersion(
          workflowTemplateId,
          workflowTemplateVersionId
        )
      );
    },
    onSelectWorkflowTemplate(workflowTemplateId: string): void {
      dispatch(WorkflowTemplateSelectionReduxActions.selectWorkflowTemplate(workflowTemplateId));
    },
  };
}

interface WorkflowTemplateSelectionTableRowProps extends DispatchProps {
  row: WorkflowTemplateSelectionTableRow;
}

const _WorkflowTemplateSelectionTableRow: React.FC<WorkflowTemplateSelectionTableRowProps> = ({
  row,
  onSelectWorkflowTemplateVersion,
  onSelectWorkflowTemplate,
}: WorkflowTemplateSelectionTableRowProps): JSX.Element => {
  const {isSelected, workflowTemplateId, workflowTemplateName, selectedVersionId, templateVersions} = row;
  const selectedVersion = _.findWhere(templateVersions, {
    id: selectedVersionId,
  })!;

  return (
    <tr
      className="workflows-workflowTemplateSelectionTable-row"
      onClick={() => {
        onSelectWorkflowTemplate(workflowTemplateId);
      }}
    >
      <td className={classNames('workflows-workflowTemplateSelectionTable-cell', 'scrollableTable-cell')}>
        <span className="u-padding">
          <input checked={isSelected} type="radio" onChange={noop} />
        </span>
      </td>
      <td
        className={classNames(
          'workflows-workflowTemplateSelectionTable-cell',
          'scrollableTable-cell',
          'scrollableTable-cell--isPrimary'
        )}
      >
        <span
          className={classNames({
            'workflows-workflowTemplateSelectionTable-cellText--selected': isSelected,
          })}
        >
          {workflowTemplateName}
        </span>
      </td>
      <td className={classNames('workflows-workflowTemplateSelectionTable-cell scrollableTable-cell')}>
        <div
          className="workflows-workflowTemplateSelectionTable-dropdown"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <SelectDropdown
            displayName={WorkflowTemplateUtil.getWorkflowTemplateVersionVersionLabel(selectedVersion)}
            menu={
              <Menu width={MenuWidth.MEDIUM}>
                {templateVersions.map((templateVersion) => (
                  <Option
                    key={templateVersion.id}
                    onSelect={() => {
                      onSelectWorkflowTemplateVersion(workflowTemplateId, templateVersion.id);
                    }}
                  >
                    {WorkflowTemplateUtil.getWorkflowTemplateVersionVersionLabel(templateVersion)}
                  </Option>
                ))}
              </Menu>
            }
          />
        </div>
      </td>
    </tr>
  );
};

const WorkflowTemplateSelectionTableRowComponent = connect(
  null,
  mapDispatchToProps
)(_WorkflowTemplateSelectionTableRow);

function renderRows(tableRows: Array<WorkflowTemplateSelectionTableRow>): Array<JSX.Element> {
  const rows = [];
  for (const row of tableRows) {
    rows.push(<WorkflowTemplateSelectionTableRowComponent row={row} key={row.workflowTemplateId} />);
  }
  return rows;
}

interface WorkflowTemplateSelectionTableProps {
  tableRows: Array<WorkflowTemplateSelectionTableRow>;
}

const WorkflowTemplateSelectionTable: React.FC<WorkflowTemplateSelectionTableProps> = ({
  tableRows,
}: WorkflowTemplateSelectionTableProps): JSX.Element => (
  <div className="workflows-workflowTemplateSelectionTable-container">
    <table className="workflows-workflowTemplateSelectionTable scrollableTable">
      <thead>
        <tr>
          <th className="workflows-workflowTemplateSelectionTable-headerCell scrollableTable-headerCell" />
          <th
            className="
              workflows-workflowTemplateSelectionTable-headerCell
              scrollableTable-headerCell
              scrollableTable-cell--isPrimary"
          >
            Template
          </th>
          <th className="workflows-workflowTemplateSelectionTable-headerCell scrollableTable-headerCell">
            Version
          </th>
        </tr>
      </thead>
      <tbody>{renderRows(tableRows)}</tbody>
    </table>
  </div>
);

export default WorkflowTemplateSelectionTable;
